<template>
  <div class="ui-date-input">
    <ui-input
      class="ui-date-input__input"
      :model-value="modelValue"
      :mask="mask"
      :label="label"
      :placeholder="placeholder"
      :clearable="clearable"
      :readonly="readonly"
      @update:model-value="updateDates"
      @click.stop="openDatepicker"
    />
    <transition name="fade">
      <v-date-picker
        v-if="isDatepickerVisible"
        ref="datepicker"
        v-model="dates"
        v-click-outside="closeDatepicker"
        show-adjacent-months
        class="ui-date-input__datepicker"
        multiple="range"
        color="primary"
        theme="fundayTheme"
        hide-header
        :min="min"
        :max="max"
        @update:model-value="updateDates"
      />
    </transition>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, PropType,
} from 'vue';

import { ddmmyyyy } from '@/utils/date/date-to-string';

export default defineComponent({
  name: 'ui-date-input',

  props: {
    modelValue: {
      type: String as PropType<string>,
      default: '',
    },

    mask: {
      type: String as PropType<string>,
      default: '',
    },

    label: {
      type: String as PropType<string>,
      default: '',
    },

    placeholder: {
      type: String as PropType<string>,
      default: '',
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    clearable: {
      type: Boolean,
      default: false,
    },

    min: {
      type: [Date, String] as PropType<Date | string>,
      default: new Date(),
    },

    max: {
      type: [Date, String] as PropType<Date | string>,
      default: new Date(),
    },
  },

  emits: ['update:modelValue', 'mask-completed', 'date-input-opened'],

  data: () => ({
    isDatepickerVisible: false,
    dates: [],
  }),

  methods: {
    updateDates(dates: string | string[]) {
      if (!dates || dates.length === 0) {
        this.dates = [];
        this.$emit('update:modelValue', '');
        this.$emit('mask-completed', `${ddmmyyyy(new Date())} - ${ddmmyyyy(new Date())}`);
      } else if (typeof dates === 'string') {
        this.$emit('update:modelValue', dates);
        this.$emit('mask-completed', dates);
      } else if (Array.isArray(dates) && dates.length >= 2) {
        const dateFrom = new Date(dates[0]);
        const dateTo = new Date(dates[dates.length - 1]);
        const interval = `${ddmmyyyy(dateFrom)} - ${ddmmyyyy(dateTo)}`;

        this.$emit('update:modelValue', interval);
        this.$emit('mask-completed', interval);

        setTimeout(() => {
          this.isDatepickerVisible = false;
        }, 300);
      }
    },

    openDatepicker() {
      this.isDatepickerVisible = true;
      this.$emit('date-input-opened');
    },

    closeDatepicker() {
      if (this.dates?.length === 1) this.dates = [];

      this.isDatepickerVisible = false;
    },
  },
});
</script>

<style lang="scss">
.ui-date-input {
  position: relative;

  &__input {
    cursor: pointer;
    width: 99.2%;

    &.ui-input.readonly {
      .ui-input__wrp {
        background: $white;
      }

      .ui-input__native {
        background-color: $white;
        box-shadow: 0 0 0 1000px $white inset;
      }

      .ui-input__wrp:hover {
        border-color: $gray_l !important;
      }
    }
  }

  &__datepicker {
    position: absolute;
    top: 68px;
    left: 0.7%;
    box-shadow:
      0 10px 13px -6px rgba(0,0,0,.2),
      0 20px 31px 3px rgba(0,0,0,.14),
      0 8px 38px 7px rgba(0,0,0,.12) !important;

    .v-btn__content {
      font-family: Gilroy, sans-serif;
      font-size: 14px;
      font-weight: 600;
      line-height: 20px;
      text-align: center;
      text-transform: capitalize;
    }

    .v-date-picker-month__weekday {
      font-family: Gilroy, sans-serif;
      font-size: 12px;
      font-weight: 400;
      line-height: 20px;
      text-align: center;
    }

    .v-btn--icon {
      border-radius: 20%;
    }

    .v-btn--variant-outlined {
      border: none;
      border-radius: 20%;
      border-bottom: 3px solid $blue;
    }

    .v-date-picker-years {
      overflow-y: auto;
    }

    .mdi {
      color: $blue;
    }
  }

  @include breakpoint("tablet") {
    &__input {
      width: 396px;
    }

    &__datepicker {
      position: absolute;
      top: 48px;
      left: 67px;
    }
  }

  @include breakpoint("desktop") {
    &__datepicker {
      .v-btn:hover {
        .v-btn__content {
          @include stacking("layout");
          color: #fff !important;
        }
      }
    }
  }
}
</style>
